import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  DriverDataPayload,
  fleetService,
} from "../../../api/fleetService/fleetServicesRegistered";
import { useBusinessProfile } from "../../../utils/hooks/useBusinessProfile";
import { useAuth } from "../../../utils/hooks/useAuth";
import { toast } from "react-toastify";

interface Driver {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  status:string;
}

interface DriverEditDialogProps {
  open: boolean;
  onClose: () => void;
  driver: Driver;
  setDriver: (driver: Driver) => void;
  setDriverDataList: React.Dispatch<React.SetStateAction<Driver[]>>;
}

const DriverEditDialog: React.FC<DriverEditDialogProps> = ({
  open,
  onClose,
  driver,
  setDriver,
  setDriverDataList,
}) => {
  const [editedDriver, setEditedDriver] = useState<Driver>(driver);
  const [emailError, setEmailError] = useState<string | null>(null); // Stato per il messaggio di errore email
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const { businessProfile } = useBusinessProfile();
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Email non valida");
      return false;
    }
    setEmailError(null);
    return true;
  };
  const isSaveDisabled = !editedDriver.firstName.trim() || !editedDriver.lastName.trim() || dataLoading;


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedDriver({ ...editedDriver, [e.target.name]: e.target.value });
    if (e.target.name === "email") {
      validateEmail(e.target.value);
    }
  };

  const handleSave = async () => {
    if (!validateEmail(editedDriver.email)) {
      return; // Blocca il salvataggio se l'email non è valida
    }

    const driverData: DriverDataPayload = {
      legacyCodes: {
        legacyCode: businessProfile.legacycodes[0].legacycode,
        company: businessProfile.legacycodes[0].company,
      },
      firstName: editedDriver.firstName,
      lastName: editedDriver.lastName,
      phone: editedDriver.phone,
      email: editedDriver.email,
      user: user?.email || "-", // Da rendere dinamico
      status: editedDriver.status ,
    };

    try {
      setDataLoading(true);
      await fleetService.updateDriverById(driverData);

      // Aggiorna lo stato locale solo se la chiamata ha successo
      setDriver(editedDriver);
      setDriverDataList((prev) =>
        prev.map((d) => (d.email === driver.email ? editedDriver : d))
      );

      onClose();
      toast.success("Driver aggiornato con successo!");
    } catch (error) {
      console.error("Errore nell'aggiornamento del driver", error);
      toast.error("Errore nell'aggiornamento del driver");
    } finally {
      setDataLoading(false);
    }
  };
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{ width: "500px", marginLeft: "auto", marginRight: "auto" }}
    >
      <DialogTitle
        sx={{
          fontSize: "18px",
          fontWeight: "600",
        }}
      >
        Modifica Driver
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            columnGap: "16px",
          }}
        >
          <Box sx={{ width: "50%" }}>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#CF0A2C",
                fontWeight: "bold",
                textTransform: "uppercase",
                marginBottom: "4px",
              }}
            >
              {t("screens.fleetAndRoute.headerDriver.name")}
            </Typography>
            <TextField
              fullWidth
              name="firstName"
              value={editedDriver.firstName}
              onChange={handleChange}
            />
          </Box>

          <Box sx={{ width: "50%" }}>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#CF0A2C",
                fontWeight: "bold",
                textTransform: "uppercase",
                marginBottom: "4px",
              }}
            >
              {t("screens.fleetAndRoute.headerDriver.surname")}
            </Typography>
            <TextField
              fullWidth
              name="lastName"
              value={editedDriver.lastName}
              onChange={handleChange}
            />
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: "14px",
            color: "#CF0A2C",
            fontWeight: "bold",
            textTransform: "uppercase",
            marginBottom: "4px",
            marginTop: "16px",
          }}
        >
          {t("screens.fleetAndRoute.headerDriver.phone")}
        </Typography>
        <TextField
          fullWidth
          name="phone"
          value={editedDriver.phone}
          onChange={handleChange}
          disabled
        />

        <Typography
          sx={{
            fontSize: "14px",
            color: "#CF0A2C",
            fontWeight: "bold",
            textTransform: "uppercase",
            marginBottom: "4px",
            marginTop: "16px",
          }}
        >
          {t("screens.fleetAndRoute.headerDriver.email")}
        </Typography>
        <TextField
          fullWidth
          name="email"
          value={editedDriver.email}
          onChange={handleChange}
          error={!!emailError} // Mostra errore se emailError è presente
          helperText={emailError} // Messaggio di errore
          disabled
          sx={{
            "& .MuiOutlinedInput-root": {
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#333333", // Nessun bordo al passaggio del mouse
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#333333", // Nessun bordo al focus
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ marginTop: "40px" , padding:3}}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          sx={{ color: "#333333", borderColor: "#333333" }}
        >
          {t("screens.fleetAndRoute.headerDriver.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          sx={{ backgroundColor: "#CF0A2C" }}
          variant="contained"
          disabled={dataLoading || !editedDriver.firstName.trim() || !editedDriver.lastName.trim() }
        >
          {t("screens.fleetAndRoute.headerDriver.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverEditDialog;
